import React, { useEffect, useState } from "react";
import { KeyValue } from "@/main/models/common";
import { useForm } from "react-hook-form";
import { getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { getPartType, usePartTypeReducer } from "@/main/store/partType";
import { useDispatch } from "react-redux";
import { getManufacturers, useManufacturerReducer } from "@/main/store/vehicle/manufacturer";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { getClaimType, useClaimTypeReducer } from "@/main/store/claimType";
import KogAutoCompleteVehicleControlled from "@/main/components/shared/inputAutoComplete/autoCompleteVehicleControlled";
import { toast } from "react-toastify";
import { PurchaseCriteriaRequest } from "@/main/models/request/purchaseCriteriaRequest";
import { purchaseCriteriaApi } from "@/main/api";
import { PurchaseCriteriaResponse } from "@/main/models/response/purchaseCriteriaResponse";
import SecondaryButton from "../button/secondaryButton";
import useAsyncModalConfirmReason from "./../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

import * as S from "./styles";
import KOGAutoCompleteCheckbox, { IOption } from "../inputAutoCompleteCheck";
import KogInputText from "../inputText/inputText";

interface IPurchaseCriteriaModal {
	guidInsurer?: string | null;
	guidBodyShop?: string | null;
	dataPurchaseCriteriaGrid: PurchaseCriteriaResponse[];
	setPurchaseCriteriaGrid: React.Dispatch<React.SetStateAction<PurchaseCriteriaResponse[]>>;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
function PurchaseCriteriaModal({
	guidInsurer = null,
	guidBodyShop = null,
	setPurchaseCriteriaGrid,
	setOpen,
}: IPurchaseCriteriaModal) {
	const [ageStart, setAgeStart] = useState<string>("");
	const [ageEnd, setAgeEnd] = useState<string>("");
	const [optionsSelectedClaimType, setOptionsSelectedClaimType] = useState<IOption[]>([]);
	const [optionsSelectedPartType, setOptionsSelectedPartType] = useState<IOption[]>([]);
	const [loadingSave, setLoadingSave] = useState<boolean>(false);
	const [errorAgeStart, setErrorAgeStart] = useState<string | null>(null);
	const [errorAgeEnd, setErrorAgeEnd] = useState<string | null>(null);
	const [errorClaimType, setErrorClaimType] = useState<string | null>(null);
	const [errorPartType, setErrorPartType] = useState<string | null>(null);
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	const dispatch = useDispatch();

	const partTypeReducer = usePartTypeReducer();
	const manufacturerReducer = useManufacturerReducer();
	useCountryReducer();
	const claimTypeReducer = useClaimTypeReducer();
	const isDisabledManufacture = () => manufacturerReducer.manufacturers.data === null;

	const INITIAL_FORM_STATE_SAVE = {
		make: null as KeyValue | null,
		ageStart: "",
		ageEnd: "",
		claimType: null as KeyValue | null,
		partType: null as KeyValue | null,
	};

	type IPurchaseCriteriaFormSave = typeof INITIAL_FORM_STATE_SAVE;
	const { control, setValue, reset, handleSubmit } = useForm<IPurchaseCriteriaFormSave>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE_SAVE,
	});

	function validateField(
		value: IOption[] | string,
		setError: React.Dispatch<React.SetStateAction<string | null>>,
		label: string,
	) {
		if (value.length === 0 || value === "") {
			setError(label);
			return false;
		}
		setError(null);
		return true;
	}

	function validateForm() {
		const isValidAgeStart = validateField(
			ageStart,
			setErrorAgeStart,
			getTranslateByKey("purchaseCriteria.yearStartNotInformed", true),
		);
		const isValidAgeEnd = validateField(
			ageEnd,
			setErrorAgeEnd,
			getTranslateByKey("purchaseCriteria.yearEndNotInformed", true),
		);
		const isValidClaimType = validateField(
			optionsSelectedClaimType,
			setErrorClaimType,
			getTranslateByKey("purchaseCriteria.claimTypeNotInformed", true),
		);
		const isValidPartType = validateField(
			optionsSelectedPartType,
			setErrorPartType,
			getTranslateByKey("purchaseCriteria.partTypeNotInformed", true),
		);

		return isValidAgeStart && isValidAgeEnd && isValidClaimType && isValidPartType;
	}

	async function onSubmitSave(data: IPurchaseCriteriaFormSave) {
		try {
			if (!validateForm()) return;
			if (Number(ageEnd) === 0) {
				setErrorAgeEnd(getTranslateByKey("purchaseCriteria.theAgeEndCannotBeZero", true));
				return false;
			}
			if (Number(ageStart) >= Number(ageEnd)) {
				setErrorAgeStart(getTranslateByKey("purchaseCriteria.endYearCannotBeLessThanStartYear", true));
				setErrorAgeEnd(getTranslateByKey("purchaseCriteria.endYearCannotBeLessThanStartYear", true));
				return false;
			}

			let resultConfirm = null;
			let filesRequest = undefined;

			if (guidInsurer || guidBodyShop) {
				resultConfirm = await getConfirmationReason(
					getTranslateByKey("information.confirmChangeTitle"),
					ReasonTypeEnum.ChangeCompany,
				);

				if (!resultConfirm.confirmed) {
					setLoadingSave(false);
					return;
				}

				if (resultConfirm.files && resultConfirm.files.length > 0) {
					filesRequest = await Promise.all(
						resultConfirm.files.map(async (item) => {
							const base64DataUrl = await toBase64(item);
							return {
								fileName: item.name,
								contentType: item.type,
								fileBase64: base64DataUrl as string,
							};
						}),
					);
				}
			}

			setLoadingSave(true);
			const requestPurchaseCriteria = removeEmptyValues({
				guidBodyShop,
				guidInsurer,
				guidVehicleManufacturer: data?.make?.key,
				yearStart: Number(ageStart),
				yearEnd: Number(ageEnd),
				guidClaimTypes: optionsSelectedClaimType.map((claimType) => {
					return claimType.key;
				}),
				guidPartTypes: optionsSelectedPartType.map((partType) => {
					return partType.key;
				}),
			}) as PurchaseCriteriaRequest;

			if (guidInsurer || guidBodyShop) {
				requestPurchaseCriteria.changeJustification = {
					guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
					reasonDescription: resultConfirm?.observation || null,
					filesUpload: filesRequest,
				};
			}

			purchaseCriteriaApi.savePurchaseCriteria(requestPurchaseCriteria).then((res) => {
				const resData = res.data.result;
				setPurchaseCriteriaGrid((prevState) => {
					return [...prevState, resData];
				});
				toast.success(getTranslateByKey("purchaseCriteria.purchaseCriteriaSavedSuccessfully"));

				setAgeStart("");
				setAgeEnd("");
				reset(INITIAL_FORM_STATE_SAVE);
				setOptionsSelectedClaimType([]);
				setOptionsSelectedPartType([]);
				setOpen(false);
			});
		} catch {
		} finally {
			setLoadingSave(false);
		}
	}

	useEffect(() => {
		dispatch(getPartType());
		dispatch(getCountriesUser());
		dispatch(getClaimType());
	}, []);

	useEffect(() => {
		dispatch(getManufacturers({}));
	}, []);

	function handleChangeAge(value: string, setValue: React.Dispatch<React.SetStateAction<string>>) {
		const onlyNumbers = value.replace(/\D/g, ""); // Remove characters that are not numbers
		setValue(onlyNumbers);
	}

	return (
		<>
			<S.Title>{getTranslateByKey("information.registerPurchaseCriteria")}</S.Title>
			<form onSubmit={handleSubmit(onSubmitSave)} noValidate>
				<S.Container>
					<KogAutoCompleteVehicleControlled
						className="ignore-click"
						id="id-purchaseCriteriaRegister-dropdown-manufacture"
						label={getTranslateByKey("information.make")}
						nameCustom="make"
						control={control}
						setValueCustom={setValue}
						options={manufacturerReducer.manufacturers.data}
						isLoading={manufacturerReducer.manufacturers.isLoading}
						disabled={isDisabledManufacture()}
						rulesCustom={{ required: false }}
						onChangeCustom={() => {}}
					/>
					<KogInputText
						id="id-purchaseCriteriaRegister-number-ageStart"
						name="ageStart"
						label={getTranslateByKey("information.ageStartYear")}
						hasOnlyNumber
						required
						value={ageStart}
						onChangeCustom={(e) => {
							handleChangeAge(e, setAgeStart);
							validateField(e, setErrorAgeStart, getTranslateByKey("purchaseCriteria.yearStartNotInformed", true));
						}}
						errorMessageCustom={errorAgeStart}
						inputProps={{ maxLength: 3 }}
					/>
					<KogInputText
						id="id-purchaseCriteriaRegister-number-ageEnd"
						name="ageEnd"
						label={getTranslateByKey("information.ageEndYear")}
						hasOnlyNumber
						required
						value={ageEnd}
						onChangeCustom={(e) => {
							handleChangeAge(e, setAgeEnd);
							validateField(e, setErrorAgeEnd, getTranslateByKey("purchaseCriteria.yearEndNotInformed", true));
						}}
						errorMessageCustom={errorAgeEnd}
						inputProps={{ maxLength: 3 }}
					/>
					<KOGAutoCompleteCheckbox
						id="id-purchaseCriteriaRegister-multiSelect-claimType"
						classNameCustom="ignore-click"
						label={getTranslateByKey("information.claimType")}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						options={claimTypeReducer.claimType.data || []}
						isLoading={claimTypeReducer.claimType.isLoading}
						valueCustom={optionsSelectedClaimType}
						setValueCustom={setOptionsSelectedClaimType}
						onChangeCustom={(value) => {
							validateField(value, setErrorClaimType, getTranslateByKey("purchaseCriteria.claimTypeNotInformed", true));
						}}
						errorMessageCustom={errorClaimType}
						customInputProps={{
							required: true,
						}}
					/>
					<KOGAutoCompleteCheckbox
						id="id-purchaseCriteriaRegister-multiSelect-partTypes"
						classNameCustom="ignore-click"
						label={getTranslateByKey("information.partTypes")}
						selectAllLabel={getTranslateByKey("information.selectAll")}
						options={partTypeReducer.partType.data || []}
						isLoading={partTypeReducer.partType.isLoading}
						valueCustom={optionsSelectedPartType}
						setValueCustom={setOptionsSelectedPartType}
						onChangeCustom={(value) => {
							validateField(value, setErrorPartType, getTranslateByKey("purchaseCriteria.partTypeNotInformed", true));
						}}
						errorMessageCustom={errorPartType}
						customInputProps={{
							required: true,
						}}
					/>
				</S.Container>
				<S.AreaButton>
					<SecondaryButton id="id-purchaseCriteria-button-save" type="submit" isLoading={loadingSave}>
						{getTranslateByKey("information.save")}
					</SecondaryButton>
				</S.AreaButton>
			</form>
			<ModalConfirmationReason closeByOverlay={false} />
		</>
	);
}

export default PurchaseCriteriaModal;
